import React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import styles from './SocialChatList.scss';
import { connect } from 'react-redux';
import {
  withChatrooms,
  AVAILABILITY_STATUS,
  Avatar,
  InjectedChatroomsProps,
} from '@wix/chat-web';
import SocialEmptyListState from '../SocialEmptyListState/SocialEmptyListState';
import ChatroomListEntry from './ChatroomListEntry/ChatroomListEntry';
import Button from '../Button';
import {
  editorSettingsAvatar,
  getLocalizedTexts,
  headerFontFamily,
  messagesFontFamily,
  primaryColorOrDefault,
  textColorOrDefault,
} from '../../reducers';
import utils from '../../services/utils';
import { AVATARS } from '../../constants/avatars';

interface SocialChatListProps
  extends InjectedTranslateProps,
    InjectedChatroomsProps {
  onSelectChatroom(roomId: string): void;
  onClickCreateNewChat(): void;
  businessChatroomId: string;
  localizedTexts: any;
  editorSettingsAvatar: Avatar;
  textColor: string;
  includeBusinessChatroom: boolean;
  primaryColor: string;
  availabilityStatus: any;
  messageFont: string;
  headerFont: string;
}

class SocialChatList extends React.Component<SocialChatListProps> {
  shouldShowEmptyState() {
    const { chatrooms, businessChatroomId } = this.props;
    return (
      chatrooms.length === 0 ||
      (chatrooms.length === 1 && chatrooms[0].id === businessChatroomId)
    );
  }

  isSocialChatroom = (chatroom) => !this.isBusinessChatroom(chatroom);

  isBusinessChatroom = ({ id }) => id === this.props.businessChatroomId;

  toSocialChatroomEntry = (chatroom) => {
    const { onSelectChatroom, primaryColor } = this.props;
    const avatar = chatroom.thumbnail
      ? { ...AVATARS.IMG.prop, imgUrl: chatroom.thumbnail }
      : AVATARS.NONE.prop;

    return (
      <div
        key={chatroom.id}
        style={{ background: `${utils.getRgbA(primaryColor, 0.1)}` }}
      >
        <ChatroomListEntry
          key={chatroom.id}
          onClick={onSelectChatroom}
          room={chatroom}
          avatar={avatar}
          shouldHaveDivider={true}
        />
      </div>
    );
  };

  toBusinessChatroom = (chatroom) => {
    const { onSelectChatroom, primaryColor } = this.props;
    return (
      <div
        key={chatroom.id}
        style={{ background: `${utils.getRgbA(primaryColor, 0.1)}` }}
      >
        <ChatroomListEntry
          key={chatroom.id}
          onClick={onSelectChatroom}
          room={chatroom}
          avatar={this.props.editorSettingsAvatar}
        />
      </div>
    );
  };

  render() {
    const {
      t,
      chatrooms,
      onClickCreateNewChat,
      businessChatroomId,
      localizedTexts: { onlineTitle, offlineTitle },
      textColor,
      includeBusinessChatroom,
      availabilityStatus,
      messageFont,
      headerFont,
    } = this.props;

    let businessChatroom;
    if (includeBusinessChatroom) {
      const businessChatroomFromList = chatrooms.find(this.isBusinessChatroom);
      const isAvailable = availabilityStatus !== AVAILABILITY_STATUS.AWAY;
      const titleForBusinessRoom = isAvailable ? onlineTitle : offlineTitle;
      if (businessChatroomFromList) {
        const { id, count, timestamp, online, text } = businessChatroomFromList;
        businessChatroom = {
          id,
          name: titleForBusinessRoom,
          count,
          timestamp,
          online,
          text,
        };
      } else {
        businessChatroom = {
          id: businessChatroomId,
          name: titleForBusinessRoom,
        };
      }
    }

    const newChatButton = (
      <div className={styles.newChat} style={{ fontFamily: headerFont }}>
        <Button dataHook="new-chat-button" onClick={onClickCreateNewChat}>
          {t('social.new-chat-button')}
        </Button>
      </div>
    );

    const renderBusinessChatroomIfExist = includeBusinessChatroom && (
      <div
        data-hook="business-chatroom"
        style={{
          borderBottom: `3px solid ${utils.getRgbA(textColor, 0.1)}`,
        }}
      >
        {this.toBusinessChatroom(businessChatroom)}
      </div>
    );
    return (
      <div
        className={styles.root}
        data-hook="social-chat-list-wrapper"
        style={{ fontFamily: messageFont }}
      >
        <div className={styles.chatRoomList}>
          <div className={styles.listWrapper}>
            {renderBusinessChatroomIfExist}
            {chatrooms
              .filter(this.isSocialChatroom)
              .map(this.toSocialChatroomEntry)}
          </div>
          {this.shouldShowEmptyState() && <SocialEmptyListState />}
        </div>
        {newChatButton}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  businessChatroomId: state.businessChatRoomId,
  localizedTexts: getLocalizedTexts(state),
  editorSettingsAvatar: editorSettingsAvatar(state),
  textColor: textColorOrDefault(state),
  primaryColor: primaryColorOrDefault(state),
  availabilityStatus: state.availabilityStatus,
  messageFont: messagesFontFamily(state),
  headerFont: headerFontFamily(state),
});

export default connect(mapStateToProps)(
  translate()(withChatrooms(SocialChatList)),
);

export const ControlledSocialChatList = connect(mapStateToProps)(
  translate()(SocialChatList),
);
